/**
 * What the two time boxes on a recipe say (#850): « Préparation » (hands-on),
 * « Prêt en » (elapsed, waits included) and the one line under each.
 *
 * Pure copy logic over the numbers a recipe already carries; the component only
 * lays it out. A number with nothing behind it is `null`, never a zero.
 */
import type { TFunction } from 'i18next';
import { deriveTimes, type RecipeTimeline, type TimelineStatus } from './recipeTimeline';
import { formatDuration } from './formatDuration';

export interface TimeFacts {
  handsOn: string | null;
  ready: string | null;
  handsOnCaption: string | null;
  readyCaption: string | null;
}

type Translate = (key: string, options?: Record<string, unknown>) => unknown;

const NBSP = '\u00a0';
/** A shorter wait is the rhythm of the cooking, not something to plan around. */
const MIN_CAPTIONED_WAIT_MINUTES = 15;

/** A time never breaks between its number and its unit. */
function duration(minutes: number, t: Translate): string {
  return formatDuration(minutes, t).replace(/ /g, NBSP);
}

/** « 15 à 30 min », « 6 à 8 h », « 45 min à 1 h 30 », « jusqu'à 1 h »; two ends that match read as one time. */
function range(min: number, max: number, t: Translate): string {
  if (min === max) return duration(max, t);
  if (min === 0) return String(t('recipeTime.rangeUpTo', { max: duration(max, t) }));
  if (max < 60) return String(t('recipeTime.rangeMinutes', { min, max }));
  if (min % 60 === 0 && max % 60 === 0) {
    return String(t('recipeTime.rangeHours', { min: min / 60, max: max / 60 }));
  }
  return String(t('recipeTime.rangeMixed', { min: duration(min, t), max: duration(max, t) }));
}

const positive = (n: number | undefined): n is number => typeof n === 'number' && n > 0;

export function recipeTimeFacts(
  r: { handsOnMinutes?: number; readyInMinutes?: number; timeline?: RecipeTimeline; timelineStatus?: TimelineStatus },
  t: TFunction,
): TimeFacts {
  const tt = t as unknown as Translate;
  // A cook's own numbers are theirs alone: the timeline's captions (« dont 8 à 12 h
  // au frigo ») describe the model's reading, not the time the cook wrote.
  const derived = r.timeline && r.timelineStatus !== 'person' ? deriveTimes(r.timeline) : null;
  const handsOnMinutes = positive(r.handsOnMinutes) ? r.handsOnMinutes : derived?.handsOnMinutes;
  const readyInMinutes = positive(r.readyInMinutes) ? r.readyInMinutes : derived?.readyInMinutes;

  let handsOnCaption: string | null = null;
  let readyCaption: string | null = null;
  if (derived) {
    if (derived.moments === 1) handsOnCaption = String(tt('recipeTime.oneGo'));
    else if (derived.moments > 1) handsOnCaption = String(tt('recipeTime.moments', { count: derived.moments }));

    const wait = derived.longestWait;
    readyCaption = wait && wait.max >= MIN_CAPTIONED_WAIT_MINUTES
      ? String(tt(`recipeTime.waitCaption.${wait.wait}`, { range: range(wait.min, wait.max, tt) }))
      : String(tt('recipeTime.noWait'));
  }

  return {
    handsOn: positive(handsOnMinutes) ? duration(handsOnMinutes, tt) : null,
    ready: positive(readyInMinutes) ? duration(readyInMinutes, tt) : null,
    handsOnCaption,
    readyCaption,
  };
}

/**
 * The card's time, in two pieces so the card joins them: « Préparation 28 min »
 * only when the recipe has a timeline, « Prêt en 1 h 05 » whenever it has a time.
 */
export function recipeCardTimes(
  r: { handsOnMinutes?: number; readyInMinutes?: number },
  t: TFunction,
): { handsOn: string | null; ready: string | null } {
  const tt = t as unknown as Translate;
  return {
    handsOn: positive(r.handsOnMinutes)
      ? String(tt('recipeTime.cardHandsOn', { time: duration(r.handsOnMinutes, tt) }))
      : null,
    ready: positive(r.readyInMinutes)
      ? String(tt('recipeTime.cardReady', { time: duration(r.readyInMinutes, tt) }))
      : null,
  };
}
